import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  MapPin, Plus, Search, Edit2, Save, X, ChevronLeft, Eye,
  Building2, CreditCard, Users, Settings, RefreshCw, Loader2,
  Clock, Calendar, DollarSign, AlertCircle, CheckCircle, Package,
  ToggleLeft, ToggleRight, Info, Tag
} from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Sites Management Page - Enhanced UI
 * FRONTEND-ONLY: Uses existing backend endpoints
 */
export default function CommercialSites() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    address: "",
    vat_rate: 20.0,
    currency: "GBP",
    timezone: "Europe/London",
    active: true
  });

  // Auth check
  useEffect(() => {
    if (location.state?.user || window.location.hash?.includes('session_id=')) {
      setIsAuthenticated(true);
      setAuthLoading(false);
      return;
    }
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
      } catch (error) {
        navigate("/admin/login");
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, [navigate, location]);

  useEffect(() => { 
    if (isAuthenticated) fetchSites(); 
  }, [isAuthenticated]);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commercial/sites`, { withCredentials: true });
      setSites(res.data.sites || []);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const openAddModal = () => {
    setFormData({
      name: "",
      code: "",
      address: "",
      vat_rate: 20.0,
      currency: "GBP",
      timezone: "Europe/London",
      active: true
    });
    setShowAddModal(true);
  };

  const openDetailModal = (site) => {
    setSelectedSite(site);
    setFormData({
      name: site.name,
      code: site.code,
      address: site.address || "",
      vat_rate: site.vat_rate,
      currency: site.currency,
      timezone: site.timezone,
      active: site.active !== false
    });
    setActiveTab("info");
    setShowDetailModal(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.code) {
      alert("Name and Code are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/commercial/sites`, formData, { withCredentials: true });
      setShowAddModal(false);
      fetchSites();
    } catch (err) { 
      console.error(err); 
      alert(err.response?.data?.detail || "Error creating site"); 
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSite) return;
    setSaving(true);
    try {
      await axios.put(`${API}/commercial/sites/${selectedSite.site_id}`, formData, { withCredentials: true });
      setShowDetailModal(false);
      fetchSites();
    } catch (err) { 
      console.error(err);
      alert(err.response?.data?.detail || "Error updating site"); 
    } finally {
      setSaving(false);
    }
  };

  const toggleSiteActive = async (site) => {
    try {
      await axios.put(`${API}/commercial/sites/${site.site_id}`, { active: !site.active }, { withCredentials: true });
      fetchSites();
    } catch (err) {
      console.error(err);
      alert("Error updating site status");
    }
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = !searchTerm || 
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = showInactive || site.active !== false;
    return matchesSearch && matchesActive;
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-sites">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Sites & Configuration</h1>
              <p className="text-sm text-gray-500">Manage parking sites and their settings</p>
            </div>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="add-site-btn"
          >
            <Plus className="w-4 h-4" /> Add Site
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sites..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="site-search"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive
          </label>
          <button onClick={fetchSites} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : filteredSites.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">No sites found</p>
            <button onClick={openAddModal} className="text-blue-600 hover:underline">
              Create your first site
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSites.map((site) => (
              <div 
                key={site.site_id} 
                className={`bg-white rounded-xl border ${site.active !== false ? 'border-gray-200' : 'border-gray-300 bg-gray-50'} overflow-hidden hover:shadow-md transition-shadow`}
                data-testid={`site-card-${site.site_id}`}
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${site.active !== false ? 'bg-teal-100' : 'bg-gray-200'} flex items-center justify-center`}>
                        <MapPin className={`w-5 h-5 ${site.active !== false ? 'text-teal-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{site.name}</h3>
                        <p className="text-xs text-gray-500 font-mono">{site.code}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => toggleSiteActive(site)}
                      className="p-1 hover:bg-gray-100 rounded"
                      title={site.active !== false ? "Disable site" : "Enable site"}
                    >
                      {site.active !== false ? (
                        <ToggleRight className="w-6 h-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {site.address && (
                    <p className="text-sm text-gray-500 mb-3">{site.address}</p>
                  )}
                  
                  {/* Site Info Grid */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">VAT: {site.vat_rate}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{site.currency}</span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    {site.active !== false ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                        <AlertCircle className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Card Actions */}
                <div className="border-t border-gray-100 p-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex gap-1">
                    <button 
                      onClick={() => navigate(`/admin/commercial/products-tariffs?site_id=${site.site_id}`)}
                      className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-blue-600"
                      title="Products & Tariffs"
                    >
                      <Package className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => navigate(`/admin/commercial/payments?site_id=${site.site_id}`)}
                      className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-green-600"
                      title="Payments"
                    >
                      <CreditCard className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => navigate(`/admin/commercial/resident-passes?site_id=${site.site_id}`)}
                      className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-purple-600"
                      title="Passes"
                    >
                      <Users className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => openDetailModal(site)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                      data-testid={`configure-${site.site_id}`}
                    >
                      <Settings className="w-4 h-4" /> Configure
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Site Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg m-4 max-h-[90vh] overflow-y-auto" data-testid="add-site-modal">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Create New Site</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Kyle Rise Car Park"
                  data-testid="modal-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                  placeholder="e.g., KYLERISE"
                  data-testid="modal-code"
                />
                <p className="text-xs text-gray-500 mt-1">Unique identifier, auto-uppercased</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 123 Main Street, Glasgow"
                  data-testid="modal-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
                  <input
                    type="number"
                    value={formData.vat_rate}
                    onChange={(e) => setFormData({...formData, vat_rate: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max="100"
                    step="0.1"
                    data-testid="modal-vat"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="modal-currency"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="modal-timezone"
                >
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="Europe/Dublin">Europe/Dublin</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button 
                onClick={() => setShowAddModal(false)} 
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreate} 
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="modal-create"
              >
                {saving ? "Creating..." : "Create Site"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site Detail/Configure Modal */}
      {showDetailModal && selectedSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto" data-testid="site-detail-modal">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{selectedSite.name}</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedSite.code}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-4">
              <div className="flex gap-1">
                {[
                  { id: "info", label: "Basic Info", icon: Building2 },
                  { id: "tariff", label: "Tariff Config", icon: DollarSign },
                  { id: "hours", label: "Operating Hours", icon: Clock }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTab === tab.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" /> {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4">
              {/* Basic Info Tab */}
              {activeTab === "info" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
                      <input
                        type="number"
                        value={formData.vat_rate}
                        onChange={(e) => setFormData({...formData, vat_rate: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="GBP">GBP (£)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Europe/London">Europe/London (GMT/BST)</option>
                      <option value="Europe/Dublin">Europe/Dublin</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="active" className="text-sm text-gray-700">Site is active</label>
                  </div>
                </div>
              )}

              {/* Tariff Config Tab */}
              {activeTab === "tariff" && (
                <div className="space-y-4">
                  {/* Existing fields */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Current Configuration</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">VAT Rate:</span>
                        <span className="ml-2 font-medium">{selectedSite.vat_rate}%</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Currency:</span>
                        <span className="ml-2 font-medium">{selectedSite.currency}</span>
                      </div>
                    </div>
                  </div>

                  {/* Future fields - Not yet supported */}
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800">Additional Configuration</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          The following settings require backend support and are not yet configurable:
                        </p>
                        <ul className="mt-2 space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>Grace Period:</span>
                            <span className="text-amber-600 font-medium">Not Configured</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>Free Period:</span>
                            <span className="text-amber-600 font-medium">Not Configured</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-600">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <span>Banded Tariff:</span>
                            <span className="text-amber-600 font-medium">Requires Backend Support</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/admin/commercial/products-tariffs?site_id=${selectedSite.site_id}`)}
                    className="w-full px-4 py-3 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-2"
                  >
                    <Package className="w-4 h-4" />
                    Manage Products & Tariffs for this Site
                  </button>
                </div>
              )}

              {/* Operating Hours Tab */}
              {activeTab === "hours" && (
                <div className="space-y-4">
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800">Operating Hours Configuration</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          Operating hours and charging hours configuration requires backend support.
                        </p>
                        <ul className="mt-3 space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Operating Hours:</span>
                            <span className="text-amber-600 font-medium">Not Configured</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>Charging Hours:</span>
                            <span className="text-amber-600 font-medium">Not Configured</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Bank Holiday Settings:</span>
                            <span className="text-amber-600 font-medium">Not Configured</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Contact support to configure operating hours for this site.
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button 
                onClick={() => setShowDetailModal(false)} 
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleUpdate}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="modal-save"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
